import React, {useState} from "react";
import {ButtonPrimary, ButtonSecondary, Heading} from "pac-ux-components";
import Add24 from "pac-ux-components/build/Icons/Navigation/Add24";
import Loading from "../Loading";
import useOfflinePolicies from "../../hooks/useOfflinePolicies";
import useTenants from "../../hooks/useTenants";
import PolicyFormModal from "./PolicyFormModal";
import ForexRateModal from "./ForexRateModal";
import {formatInstant, formatLimit, LIMIT_FIELDS, shortId} from "./policyLimits";

const headerRowStyle = {fontWeight: 600, height: 48, fontSize: 16, fontFamily: 'Inter', border: "thin solid #E0E0E0"};
const rowStyle = (index) => ({
    height: 48, fontSize: 16, fontWeight: 400,
    backgroundColor: index % 2 === 0 ? '#EDEDED' : '#FFFFFF',
    border: "thin solid #E0E0E0"
});
const metaStyle = {fontSize: 12, color: '#5A6B7B'};
const disabledBadgeStyle = {fontSize: 12, padding: '2px 6px', borderRadius: 2, background: '#B00020', color: '#FFFFFF'};

const isAirlinePolicy = (policy) => policy.scope === 'AIRLINE' || !policy.tailNumber;

/**
 * "Offline Risk" module (PYMNT-2590 NFC Ground Offline, design 5.4 / 11): one airline policy per ICAO
 * with the tail overrides listed beneath it (only deviating aircraft have a row, bold cells are the limits
 * the tail overrides), and the exchange rates the signed policy ships with. Policies are immutable: every
 * save creates a new policy id and "Retire" keeps the row for the deferred charges that reference it.
 */
function OfflineRiskPolicies() {
    const {policies, rates, loading, error, setError, savePolicy, retirePolicy, saveRate} = useOfflinePolicies();
    const {tenants, loading: tenantsLoading} = useTenants();
    // Airline choices come from the ICAO code configured on each enabled tenant (one entry per code).
    const icaoOptions = Array.from(new Set(tenants
        .filter((tenant) => tenant.enabled !== false && tenant.airlineIcao)
        .map((tenant) => tenant.airlineIcao.toUpperCase())))
        .sort()
        .map((icao) => ({value: icao, label: icao, disabled: false}));
    const [editing, setEditing] = useState(null); // {airlineIcao, baseline, policy, isBaseline}
    const [editingRate, setEditingRate] = useState(null); // {rate} | {rate: null}
    const [notice, setNotice] = useState(null);

    if (loading || tenantsLoading) return (<Loading/>);
    if (error) return <p>Error: {error}</p>;

    const airlines = {};
    policies.filter((p) => p.current !== false).forEach((p) => {
        const icao = p.airlineIcao;
        if (!airlines[icao]) {
            airlines[icao] = {baseline: null, tails: []};
        }
        if (isAirlinePolicy(p)) {
            airlines[icao].baseline = p;
        } else {
            airlines[icao].tails.push(p);
        }
    });

    const closeModal = () => setEditing(null);

    const onRetire = async (policy) => {
        const label = isAirlinePolicy(policy)
            ? `airline policy ${policy.airlineIcao} (${shortId(policy.id)})`
            : `tail override ${policy.tailNumber} (${shortId(policy.id)})`;
        const consequence = isAirlinePolicy(policy)
            ? 'The aircraft of this airline will decline all offline payments until a new airline policy exists.'
            : 'The aircraft returns to the airline policy.';
        // eslint-disable-next-line no-restricted-globals
        if (!window.confirm(`Retire ${label}? The policy is kept for the deferred charges that reference it but is no longer served. ${consequence}`)) {
            return;
        }
        setNotice(null);
        const problem = await retirePolicy(policy.id);
        setNotice(problem);
    };

    const policyMeta = (policy) => (<span style={metaStyle} title={policy.id}>
        {shortId(policy.id)} · {formatInstant(policy.createdAt)}{policy.createdBy ? ` · ${policy.createdBy}` : ''}
    </span>);

    return (<div className="OfflineRisk" style={{width: 1047}}>
        <Heading color={'#10304C'} style={{paddingTop: 56, paddingBottom: 16}}>Offline Risk</Heading>
        <div style={{float: 'right', marginTop: '-50px'}}>
            <ButtonPrimary onClick={() => setEditing({airlineIcao: null, baseline: null, policy: null, isBaseline: true})}>
                <Add24/> Add airline policy
            </ButtonPrimary>
        </div>
        <p style={{fontFamily: 'Inter', fontSize: 14, color: '#5A6B7B'}}>
            Limits an aircraft applies to NFC payments taken while offline, in USD. Every airline needs an airline
            policy; a tail override changes only the limits it sets and inherits the rest. Every save creates a new
            policy id, which is what a deferred charge reports back.
        </p>
        {notice && (<div style={{color: '#B00020', marginBottom: 12, fontSize: 14}}>{notice}</div>)}

        {Object.keys(airlines).sort().map((icao) => {
            const {baseline, tails} = airlines[icao];
            return (<div key={icao} style={{marginBottom: 40}}>
                <div style={{display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8}}>
                    <Heading color={'#10304C'} style={{fontSize: 20}}>{icao}</Heading>
                    {baseline && baseline.enabled === false && (<span style={disabledBadgeStyle}>disabled</span>)}
                    {baseline && (<ButtonSecondary onClick={() => setEditing({airlineIcao: icao, baseline, policy: baseline, isBaseline: true})}>
                        Edit airline policy
                    </ButtonSecondary>)}
                    {baseline && (<ButtonSecondary onClick={() => setEditing({airlineIcao: icao, baseline, policy: null, isBaseline: false})}>
                        <Add24/> Add tail override
                    </ButtonSecondary>)}
                    {baseline && tails.length === 0 && (<ButtonSecondary onClick={() => onRetire(baseline)}>Retire</ButtonSecondary>)}
                    {baseline && tails.length > 0 && (<span style={metaStyle} title="The backoffice refuses to retire an airline policy that still has current tail overrides (409 POLICY_IN_USE)">
                        Retire the tail overrides before retiring the airline policy
                    </span>)}
                </div>
                {!baseline && (<div style={{color: '#B00020', fontSize: 14, marginBottom: 8}}>
                    Tail overrides exist but the airline policy is missing; the aircraft will decline all offline payments.
                    Add the airline policy for {icao} to restore them.
                </div>)}
                <table width="1047px" className="Table">
                    <thead>
                    <tr style={headerRowStyle}>
                        <td width="130" style={{paddingLeft: 32}}>Tail</td>
                        <td width="210" style={{fontSize: 13}}>Policy id · created</td>
                        {LIMIT_FIELDS.map(({key, label}) => (<td key={key} style={{fontSize: 13}}>{label}</td>))}
                        <td width="90"/>
                    </tr>
                    </thead>
                    <tbody>
                    {baseline && (<tr style={rowStyle(0)}>
                        <td style={{paddingLeft: 32, fontWeight: 600}}>Airline</td>
                        <td>{policyMeta(baseline)}</td>
                        {LIMIT_FIELDS.map(({key, money}) => (<td key={key}>{formatLimit(baseline.limits ? baseline.limits[key] : null, money)}</td>))}
                        <td/>
                    </tr>)}
                    {tails.map((tail, index) => {
                        const overriddenLimits = tail.overriddenLimits || [];
                        const limits = tail.limits || {};
                        return (<tr key={tail.id} style={rowStyle(index + 1)}>
                            <td style={{paddingLeft: 32}}>
                                <span onClick={() => setEditing({airlineIcao: icao, baseline, policy: tail, isBaseline: false})}
                                      style={{cursor: 'pointer'}}>{tail.tailNumber}</span>
                                {tail.enabled === false && (<span style={{...disabledBadgeStyle, marginLeft: 8}}>disabled</span>)}
                            </td>
                            <td>{policyMeta(tail)}</td>
                            {LIMIT_FIELDS.map(({key, money}) => {
                                const overridden = overriddenLimits.includes(key);
                                return (<td key={key} style={{fontWeight: overridden ? 600 : 400, color: overridden ? '#10304C' : '#7A8794'}}
                                            title={overridden ? 'Overridden by this tail' : 'Inherited from the airline policy'}>
                                    {formatLimit(limits[key], money)}
                                </td>);
                            })}
                            <td>
                                <span onClick={() => onRetire(tail)} style={{cursor: 'pointer', color: '#B00020'}}>Retire</span>
                            </td>
                        </tr>);
                    })}
                    </tbody>
                </table>
            </div>);
        })}

        <div style={{display: 'flex', alignItems: 'center', gap: 16, marginTop: 24, marginBottom: 8}}>
            <Heading color={'#10304C'} style={{fontSize: 20}}>Exchange rates to USD</Heading>
            <ButtonSecondary onClick={() => setEditingRate({rate: null})}><Add24/> Add rate</ButtonSecondary>
        </div>
        <p style={{fontFamily: 'Inter', fontSize: 14, color: '#5A6B7B'}}>
            USD per one unit of each currency, shipped inside the signed policy so the aircraft can apply the USD limits offline.
        </p>
        <table width="1047px" className="Table">
            <thead>
            <tr style={headerRowStyle}>
                <td width="120" style={{paddingLeft: 32}}>ISO</td>
                <td width="220">Rate (USD per unit)</td>
                <td width="220">Name</td>
                <td>Updated</td>
                <td width="200">Updated by</td>
            </tr>
            </thead>
            <tbody>
            {rates.map((rate, index) => (<tr key={rate.iso} style={rowStyle(index)}>
                <td style={{paddingLeft: 32}}>
                    <span onClick={() => setEditingRate({rate})} style={{cursor: 'pointer'}}>{rate.iso}</span>
                </td>
                <td>{Number(rate.rate).toFixed(8)}</td>
                <td>{rate.name || '—'}</td>
                <td>{formatInstant(rate.updatedAt)}</td>
                <td>{rate.updatedBy || '—'}</td>
            </tr>))}
            </tbody>
        </table>

        {editing !== null && (<PolicyFormModal
            display={true}
            airlineIcao={editing.airlineIcao}
            baseline={editing.baseline}
            policy={editing.policy}
            isBaseline={editing.isBaseline}
            icaoOptions={icaoOptions}
            handleSave={async (request) => {
                setError(null);
                return await savePolicy(request);
            }}
            closeModal={closeModal}/>)}

        {editingRate !== null && (<ForexRateModal
            display={true}
            rate={editingRate.rate}
            handleSave={saveRate}
            closeModal={() => setEditingRate(null)}/>)}
    </div>);
}

export default OfflineRiskPolicies;
